import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Star, Loader2, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ReviewCard, { type ReviewData } from "@/components/ReviewCard";

interface SellerReviewsResponse {
  reviews: ReviewData[];
  averageRating: number;
  reviewCount: number;
  aiSummary?: string | null;
}

type SortOption = "recent" | "highest" | "lowest";

const PREVIEW_COUNT = 3;

function sortReviews(reviews: ReviewData[], sort: SortOption): ReviewData[] {
  const sorted = [...reviews];
  if (sort === "recent") {
    sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } else if (sort === "highest") {
    sorted.sort(
      (a, b) =>
        b.rating - a.rating ||
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  } else {
    sorted.sort(
      (a, b) =>
        a.rating - b.rating ||
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }
  return sorted;
}

function StarBar({ star, count, total }: { star: number; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-[11px] leading-none">
      <span className="w-3 text-muted-foreground">{star}</span>
      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-yellow-400/80" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right tabular-nums text-muted-foreground">{count}</span>
    </div>
  );
}

export default function SellerReviews({ sellerId }: { sellerId: string }) {
  const [isAllOpen, setIsAllOpen] = useState(false);
  const [sort, setSort] = useState<SortOption>("recent");

  const { data, isLoading } = useQuery<SellerReviewsResponse>({
    queryKey: ["/api/reviews/seller", sellerId],
    queryFn: async () => {
      const res = await fetch(`/api/reviews/seller/${sellerId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(sellerId),
  });

  const reviews = data?.reviews ?? [];
  const avg = data?.averageRating ?? 0;
  const count = data?.reviewCount ?? 0;
  const aiSummary = data?.aiSummary?.trim() || null;

  const distribution = useMemo(() => {
    const buckets = [0, 0, 0, 0, 0];
    for (const r of reviews) {
      const idx = Math.min(Math.max(Math.round(r.rating) - 1, 0), 4);
      buckets[idx] += 1;
    }
    return buckets;
  }, [reviews]);

  const preview = useMemo(
    () => sortReviews(reviews, "recent").slice(0, PREVIEW_COUNT),
    [reviews],
  );
  const sortedAll = useMemo(() => sortReviews(reviews, sort), [reviews, sort]);

  if (isLoading) {
    return (
      <Card className="mb-6 overflow-hidden border-primary/15 bg-card-gradient">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading reviews...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 overflow-hidden border-primary/15 bg-card-gradient">
      <CardContent className="pt-6">
        <div className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Community trust
        </div>

        {count > 0 ? (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-semibold leading-none">{avg.toFixed(1)}</span>
                <div className="flex flex-col gap-1">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`h-4 w-4 ${
                          s <= Math.round(avg)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground/40"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {count} review{count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                {[5, 4, 3, 2, 1].map((s) => (
                  <StarBar key={s} star={s} count={distribution[s - 1]} total={count} />
                ))}
              </div>
            </div>

            {aiSummary && (
              <div
                className="rounded-lg border border-primary/20 bg-primary/5 p-3"
                data-testid="ai-review-summary"
              >
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  <Sparkles className="h-3 w-3" />
                  Review highlights
                </div>
                <p className="text-sm leading-snug text-foreground">{aiSummary}</p>
              </div>
            )}

            <div className="divide-y divide-border">
              {preview.map((review) => (
                <div key={review.id} className="py-3 first:pt-0 last:pb-0">
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>

            {count > PREVIEW_COUNT && (
              <Dialog open={isAllOpen} onOpenChange={setIsAllOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-primary/25 text-sm hover:bg-primary/10"
                    data-testid="open-all-reviews"
                  >
                    Show all {count} reviews
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0">
                  <DialogHeader className="border-b border-border px-5 py-4">
                    <DialogTitle className="flex items-baseline gap-2 text-lg">
                      <span>{avg.toFixed(1)}</span>
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-normal text-muted-foreground">
                        · {count} review{count === 1 ? "" : "s"}
                      </span>
                    </DialogTitle>
                  </DialogHeader>

                  <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Sort by
                    </span>
                    <div className="flex gap-1">
                      {(
                        [
                          { key: "recent", label: "Most recent" },
                          { key: "highest", label: "Highest" },
                          { key: "lowest", label: "Lowest" },
                        ] as { key: SortOption; label: string }[]
                      ).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setSort(opt.key)}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                            sort === opt.key
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-muted"
                          }`}
                          data-testid={`sort-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-2">
                    {aiSummary && (
                      <div className="my-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                          <Sparkles className="h-3 w-3" />
                          Review highlights
                        </div>
                        <p className="text-sm leading-snug text-foreground">{aiSummary}</p>
                      </div>
                    )}
                    <div className="divide-y divide-border">
                      {sortedAll.map((review) => (
                        <div key={review.id} className="py-3 first:pt-0 last:pb-0">
                          <ReviewCard review={review} />
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No reviews yet</p>
        )}
      </CardContent>
    </Card>
  );
}
